import mixpanel from 'mixpanel-browser';
import { environment } from 'src/environments/environment';
import {
  AnalyticsProvider,
  EventProperties,
  UserIdentity,
  UserProperties,
} from './analytics.types';


export class MixpanelProvider implements AnalyticsProvider {
  readonly name = 'mixpanel';
  private ready = false;

  isReady(): boolean {
    return this.ready;
  }

  init(): void {
    const token = environment.analytics?.mixpanelToken;
    if (!token) {
      return;
    }
    try {
      // Region MUST match the region where the Mixpanel project lives —
      // sending events to the wrong endpoint returns 200 OK but silently
      // discards data. Config comes from `environment.analytics.mixpanelRegion`
      // so migrations only need an env change, not a provider rewrite.
      const region = (environment.analytics as any)?.mixpanelRegion || 'us';
      const apiHostByRegion: Record<string, string> = {
        us: 'https://api.mixpanel.com',
        eu: 'https://api-eu.mixpanel.com',
        in: 'https://api-in.mixpanel.com',
      };
      mixpanel.init(token, {
        debug: !!environment.analytics?.debug,
        persistence: 'localStorage',
        ignore_dnt: !!environment.analytics?.ignoreDoNotTrack,
        api_host: apiHostByRegion[region] || apiHostByRegion['us'],
        track_pageview: false, // we fire route_viewed manually so we can attach role + custom props
        loaded: () => {
          this.ready = true;
        },
      });
      // `loaded` isn't always called synchronously on init; flip ready so
      // downstream identify() calls that happen right after init() don't
      // silently drop.
      this.ready = true;
    } catch (err) {
      // Init failure is rare (bad token, blocked script). Log and stay off.
      console.warn('[analytics/mixpanel] init failed', err);
    }
  }

  identify(identity: UserIdentity): void {
    if (!this.ready) return;
    try {
      // Simplified ID Merge (Mixpanel's default since 2020): just call
      // `identify()`. Mixpanel automatically links the anonymous
      // $device_id with the real distinct_id on the server side using its
      // $identify system event — no alias() dance needed. The old
      // alias-then-identify pattern is DEPRECATED for new projects and can
      // cause User profiles to fail to appear on the Users page.
      // Docs: https://docs.mixpanel.com/docs/tracking-methods/id-management/identifying-users
      if (identity.distinctId) {
        mixpanel.identify(identity.distinctId);
      }
      if (identity.properties) {
        // People profile stores canonical user attributes. Requires the
        // caller to be `identify()`'d first — otherwise Mixpanel queues
        // the set() and flushes it after the next identify(). Our order
        // above (identify first, then people.set) means the set flushes
        // to the real distinct_id immediately.
        mixpanel.people.set(this.mapPeopleProps(identity.properties));
      }
      // Register `role` as a super-property so every subsequent event is
      // filterable by role without callers repeating themselves.
      mixpanel.register({ role: identity.role });
    } catch (err) {
      console.warn('[analytics/mixpanel] identify failed', err);
    }
  }

  setUserProperties(props: UserProperties): void {
    if (!this.ready) return;
    try {
      mixpanel.people.set(this.mapPeopleProps(props));
    } catch (err) {
      console.warn('[analytics/mixpanel] setUserProperties failed', err);
    }
  }

  track(event: string, properties?: EventProperties): void {
    if (!this.ready) return;
    try {
      mixpanel.track(event, properties || {});
    } catch (err) {
      console.warn('[analytics/mixpanel] track failed', event, err);
    }
  }

  trackRoute(path: string, title?: string): void {
    // Mixpanel doesn't have a special pageview API — a plain event with a
    // stable name is the idiomatic pattern.
    this.track('route_viewed', { path, title: title || '' });
  }

  reset(): void {
    if (!this.ready) return;
    try {
      mixpanel.reset();
    } catch (err) {
      console.warn('[analytics/mixpanel] reset failed', err);
    }
  }

  /** Mixpanel uses `$`-prefixed magic keys for a handful of built-in
   *  People properties. Map our normalised keys onto them. */
  private mapPeopleProps(props: UserProperties): Record<string, unknown> {
    const out: Record<string, unknown> = { ...props };
    if (props.email)      out['$email'] = props.email;
    if (props.name)       out['$name'] = props.name;
    if (props.phone)      out['$phone'] = props.phone;
    if (props.city)       out['$city'] = props.city;
    return out;
  }
}
